import { useEffect, useMemo, useState } from 'react';
import { getAccountLeads } from 'api/leads/leads';
import { GlobalHeader } from 'components/_global/header/GlobalHeader';
import { preferredName } from 'components/_temp_ui_common/utilities/preferredName';
import { Button } from 'components/core/button';
import { useBrowserContext } from 'contexts/BrowserContext';
import { useCurrentUser } from 'contexts/CurrentUserContext';
import { useNavigate } from 'react-router-dom';

import { useCoreOnboardingMembers } from '../../../api/account/useCoreOnboardingMembers';
import { useNextStep } from '../../../api/account/useNextStep';
import { FeatureFlags, useFlag } from '../../../hooks/useFlag';
import { CoreOnboardingPage, MemberProgram } from '../constants';
import PhoneLink from '../leads/components/PhoneLink';
import {
  LandingBackground,
  LandingBody,
  LandingContent,
  LandingEllipse,
  LandingIconText,
  LandingImage,
  LandingImageContainer,
  LandingInfo,
  LandingInfoContainer,
  LandingInfoHeader,
  LandingLeftColumn,
  LandingRightColumn,
  LandingStepHeader,
  LandingStepListContainer,
  LandingStepListEl,
  LandingStepListElHead,
  LandingStepListElIcon,
  LandingStepListElSub,
  LandingStepListElText,
  LandingSteps,
  LandingStepSubTitle,
  LandingStepTitle,
  LandingTextHeader,
  LandingTitle,
  LandingVector,
} from './LandingComponents';

const Landing = () => {
  const { isMobileView } = useBrowserContext();
  const { state: userState } = useCurrentUser();
  const push = useNavigate();

  const [referrerOrg, setReferrerOrg] = useState<string | null>(null);
  const nextStep = useNextStep();
  const navigateCoreLanding = useFlag(FeatureFlags.NavigateCoreLanding);
  const onboardingMembers = useCoreOnboardingMembers();
  const member = useMemo(() => {
    return userState.members?.find((m) => m.id === onboardingMembers?.[0]?.id);
  }, [userState.members, onboardingMembers]);

  const isAdult = userState.account?.adultInCare;
  const memberName = preferredName(member);
  const name = isAdult ? userState.account?.firstName : memberName;

  const handingButtonClick = () => {
    if (navigateCoreLanding) {
      return nextStep
        .mutateAsync({ page: CoreOnboardingPage.CoreLanding, patientId: member?.id, program: MemberProgram.CORE })
        .then((r) => {
          push(r.url);
        });
    } else {
      return push('/account/create/cc');
    }
  };

  useEffect(() => {
    const leads = getAccountLeads();
    leads
      .then((data) => setReferrerOrg(data[0]?.referrerOrgName || null))
      .catch((error) => {
        console.error('Error fetching leads:', error);
        setReferrerOrg(null);
      });
  }, []);
  return (
    <>
      <GlobalHeader user={userState.account} hideBoxShadow={true} isMobileView={isMobileView} />
      <LandingBody>
        <LandingBackground>
          <LandingContent>
            <LandingLeftColumn>
              <LandingImageContainer>
                <LandingImage />
                <LandingEllipse />
              </LandingImageContainer>
              <div>
                <LandingTitle>Welcome, {name}!</LandingTitle>
              </div>
              <LandingInfoContainer>
                <LandingInfo>
                  {isAdult && !referrerOrg && "Thank you for accepting your referral. We're so excited you're here!"}
                  {isAdult &&
                    referrerOrg &&
                    `Thank you for accepting your referral from ${referrerOrg}. We're so excited you're here!`}
                  {!isAdult &&
                    !referrerOrg &&
                    `Thank you for accepting ${memberName}'s referral. We're so excited you're here!`}
                  {!isAdult &&
                    referrerOrg &&
                    `Thank you for accepting ${memberName}'s referral from ${referrerOrg}. We're so excited you're here!`}
                </LandingInfo>
                <LandingInfo>
                  {'Please review our '}
                  <a
                    href="https://www.bendhealth.com/welcome/refer-to-bend"
                    target="_blank"
                    rel="noreferrer"
                    className="cursor-pointer hover:underline"
                    style={{ color: 'white', fontFamily: 'sofiaprosoftbold' }}
                  >
                    Welcome Guide
                  </a>
                  {' or call '}
                  <PhoneLink color="white" phone={'1-800-516-0975'} style={{ fontFamily: 'sofiaprosoftbold' }}>
                    1-800-516-0975
                  </PhoneLink>
                  {' if you have any questions about getting started.'}
                </LandingInfo>
              </LandingInfoContainer>
            </LandingLeftColumn>
            <LandingRightColumn>
              <LandingInfoHeader>
                <LandingVector />
                <LandingTextHeader>
                  Only a few short steps remain to finish enrollment and book your first appointment:
                </LandingTextHeader>
              </LandingInfoHeader>

              <LandingSteps>
                <LandingStepHeader>
                  <LandingStepTitle>Enrollment Steps</LandingStepTitle>
                  <LandingStepSubTitle>about 10 mins</LandingStepSubTitle>
                </LandingStepHeader>

                <LandingStepListContainer>
                  <LandingStepListEl>
                    <LandingStepListElIcon>
                      <LandingIconText>1</LandingIconText>
                    </LandingStepListElIcon>
                    <LandingStepListElText>
                      <LandingStepListElHead variant="body-2">Finish profile</LandingStepListElHead>
                      <LandingStepListElSub variant="body-4">
                        Tell us a bit more about {isAdult ? ' yourself' : name}. Have your insurance card available!
                      </LandingStepListElSub>
                    </LandingStepListElText>
                  </LandingStepListEl>

                  <LandingStepListEl>
                    <LandingStepListElIcon>
                      <LandingIconText>2</LandingIconText>
                    </LandingStepListElIcon>
                    <LandingStepListElText>
                      <LandingStepListElHead variant="body-2">Take Assessment</LandingStepListElHead>
                      <LandingStepListElSub variant="body-4">
                        {isAdult
                          ? "We'll ask you about symptoms to understand how you're doing."
                          : `We'll ask you and ${memberName} about symptoms to understand how you're doing.`}
                      </LandingStepListElSub>
                    </LandingStepListElText>
                  </LandingStepListEl>

                  <LandingStepListEl>
                    <LandingStepListElIcon>
                      <LandingIconText>3</LandingIconText>
                    </LandingStepListElIcon>
                    <LandingStepListElText>
                      <LandingStepListElHead variant="body-2">Schedule Appointment</LandingStepListElHead>
                      <LandingStepListElSub variant="body-4">
                        Attend your first session within days—not months. Have a credit card handy to secure your spot.
                      </LandingStepListElSub>
                    </LandingStepListElText>
                  </LandingStepListEl>

                  <LandingStepListEl style={{ marginTop: '16px' }}>
                    <Button
                      style={{
                        width: '160px',
                        height: '50px',
                      }}
                      onClick={handingButtonClick}
                    >
                      Let&apos;s go!
                    </Button>
                  </LandingStepListEl>
                </LandingStepListContainer>
              </LandingSteps>
            </LandingRightColumn>
          </LandingContent>
        </LandingBackground>
      </LandingBody>
    </>
  );
};

export default Landing;
